import React, { JSX, useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { useOverlaySocketCredential } from 'hooks/useOverlaySocketCredential'
import { OverlayShell } from 'components/OverlayShell'
import { OverlayAutoFit } from 'components/OverlayAutoFit'
import { PresenceLayer, useScenePresence } from 'components/ScenePresence'
import { ScaledStage } from 'components/ScaledStage'
import { ChatOverlayView } from 'components/ChatOverlayView'
import { EmoteWallView } from 'components/EmoteWallView'
import { MerchBoardView } from 'components/MerchBoardView'
import {
  useChannelEvents,
  useOverlayStructureEvents
} from 'hooks/useOverlaySubscription'
import {
  PollPreview,
  draftFromPoll,
  pollLiveFromSummary
} from 'components/PollPreview'
import {
  TriviaPreview,
  draftFromTrivia,
  triviaLiveFromSummary
} from 'components/TriviaPreview'
import { TugPreview, draftFromTug, tugLiveFromSummary } from 'pages/event/TugsPage'
import { ScoreboardPreview, draftFromScoreboard, scoreboardLiveFromSummary } from 'pages/event/ScoreboardPage'
import { GiveawayPreview, draftFromGiveaway, giveawayLiveFromSummary } from 'pages/event/GiveawaysPage'
import { TUG, SCOREBOARD } from 'lib/elementCanvas'
import { ELEMENT_BASE, resolveElementBox } from 'lib/sceneElementBox'
import { SceneTransition, clipGlideTransitionOf, glideTransitionOf, takeIdOf } from 'lib/scenePresence'
import { CompositeOverlayKind } from 'lib/paths'
import {
  EventOverlay,
  GiveawaySummary,
  LiveSceneSnapshot,
  PollSummary,
  SceneElement,
  ScoreboardSummary,
  TriviaSummary,
  TugSummary
} from 'types/api'
import { graphqlRequest } from 'lib/helpers'
import getOverlayForRenderQuery from 'graphql/getOverlayForRender.graphql'
import logger from 'logger'

const log = logger.extend('composite-overlay')

const BOOTSTRAP_RETRY_MS = 15000

type OverlayMeta = Omit<EventOverlay, 'scenes'>

interface OverlayRenderData {
  overlay: EventOverlay | null
  polls: PollSummary[]
  tugs: TugSummary[]
  scoreboards: ScoreboardSummary[]
  giveaways: GiveawaySummary[]
  trivias: TriviaSummary[]
}

const foldElement = <T extends { version: number }>(
  prev: Record<string, T>,
  id: string,
  incoming: T
): Record<string, T> => {
  const existing = prev[id]
  if (existing !== undefined && incoming.version <= existing.version) return prev
  return { ...prev, [id]: incoming }
}

const foldElementList = <T extends { version: number }>(
  prev: Record<string, T>,
  list: T[],
  idOf: (el: T) => string
): Record<string, T> =>
    list.reduce((acc, el) => foldElement(acc, idOf(el), el), prev)

const removeElement = <T,>(prev: Record<string, T>, id: string): Record<string, T> => {
  if (prev[id] === undefined) return prev
  return Object.fromEntries(Object.entries(prev).filter(([key]) => key !== id))
}

interface OverlayState {
  meta: OverlayMeta
  takeId: string | null
  transition: SceneTransition
}

const foldOverlayState = (
  prev: OverlayState | null,
  incoming: OverlayMeta,
  transition: SceneTransition
): OverlayState | null => {
  if (prev != null && incoming.version <= prev.meta.version) return prev
  const takeId = takeIdOf(incoming.liveScene)
  if (prev != null && takeId === prev.takeId) return { ...prev, meta: incoming }
  return { meta: incoming, takeId, transition }
}

const placesTrivia = (elementLists: SceneElement[][]): boolean =>
  elementLists.some((els) => els.some((el) => el.elementKind === 'TRIVIA'))

const TRIVIA_FEED_REFUSED_DETAIL =
  'Trivia feed refused or failed. If this repeats after every ticket refresh, ' +
  'the stream key predates trivia: reset the stream key on the Overview page, ' +
  'then re-paste the new link into OBS. Trivia keeps rendering from the bootstrap meanwhile.'

const referencedIds = (elements: SceneElement[], kind: SceneElement['elementKind']): Set<string> =>
  new Set(
    elements
      .filter((el) => el.elementKind === kind)
      .map((el) => el.elementId)
  )

const pruneDanglingRefs = <T,>(
  prev: Record<string, T>,
  referenced: Set<string>,
  hydrated: Set<string>
): Record<string, T> => {
  const isStale = (id: string): boolean => referenced.has(id) && !hydrated.has(id)
  if (!Object.keys(prev).some(isStale)) return prev
  return Object.fromEntries(Object.entries(prev).filter(([id]) => !isStale(id)))
}

const PollElement = ({ data, layoutWidth }: { data: PollSummary, layoutWidth: number }): JSX.Element => (
  <div style={{ position: 'relative', width: layoutWidth, height: 0 }}>
    <PollPreview draft={draftFromPoll(data)} live={pollLiveFromSummary(data)} layoutWidth={layoutWidth} />
  </div>
)

const TugElement = ({ data, layoutWidth }: { data: TugSummary, layoutWidth: number }): JSX.Element => (
  <div style={{ position: 'relative', width: layoutWidth, height: TUG.height }}>
    <TugPreview draft={draftFromTug(data)} live={tugLiveFromSummary(data)} layoutWidth={layoutWidth} />
  </div>
)

const BoardElement = ({ data, layoutWidth }: { data: ScoreboardSummary, layoutWidth: number }): JSX.Element => (
  <div style={{ position: 'relative', width: layoutWidth, height: SCOREBOARD.height }}>
    <ScoreboardPreview draft={draftFromScoreboard(data)} live={scoreboardLiveFromSummary(data)} layoutWidth={layoutWidth} />
  </div>
)

const TriviaElement = ({ data, layoutWidth }: { data: TriviaSummary, layoutWidth: number }): JSX.Element => (
  <div style={{ position: 'relative', width: layoutWidth, height: 0 }}>
    <TriviaPreview draft={draftFromTrivia(data)} live={triviaLiveFromSummary(data)} layoutWidth={layoutWidth} />
  </div>
)

const GiveawayElement = ({ data, layoutWidth }: { data: GiveawaySummary, layoutWidth: number }): JSX.Element => (
  <div style={{ position: 'relative', width: layoutWidth, height: 0 }}>
    <GiveawayPreview draft={draftFromGiveaway(data)} state={data.status} live={giveawayLiveFromSummary(data)} layoutWidth={layoutWidth} />
  </div>
)

const elementFrameStyle = (el: SceneElement, glyphScale: number, glideMs: number): React.CSSProperties => ({
  position: 'absolute',
  left: el.x,
  top: el.y,
  transform: `scale(${glyphScale})`,
  transformOrigin: 'top left',
  ...(glideMs > 0 ? { transition: glideTransitionOf(glideMs) } : null)
})

const clipBoxStyle = (clipW: number, clipH: number, glideMs: number): React.CSSProperties => ({
  width: clipW,
  height: clipH,
  overflow: 'hidden',
  ...(glideMs > 0 ? { transition: clipGlideTransitionOf(glideMs) } : null)
})

const MerchElementFrame = ({
  el,
  glyphScale,
  layoutWidth,
  glideMs,
  slug,
  token,
  onContentReady
}: {
  el: SceneElement
  glyphScale: number
  layoutWidth: number
  glideMs: number
  slug: string
  token: string
  onContentReady?: () => void
}): JSX.Element => {
  const readyRef = useRef(onContentReady)
  readyRef.current = onContentReady
  const hostRef = useCallback((node: HTMLDivElement | null) => {
    if (node == null || typeof ResizeObserver === 'undefined') return
    let reported = false
    const check = (): void => {
      if (reported || (node.offsetWidth === 0 && node.offsetHeight === 0)) return
      reported = true
      readyRef.current?.()
    }
    const ro = new ResizeObserver(check)
    ro.observe(node)
    check()
    return () => ro.disconnect()
  }, [])
  return (
    <div ref={hostRef} style={elementFrameStyle(el, glyphScale, glideMs)}>
      <MerchBoardView merchOverlayId={el.elementId} token={token} slug={slug} natural wrapWidth={layoutWidth} />
    </div>
  )
}

const SceneElementFrame = ({
  el,
  glideMs,
  onContentReady,
  slug,
  token,
  pollsById,
  tugsById,
  boardsById,
  giveawaysById,
  triviasById
}: {
  el: SceneElement
  glideMs: number
  onContentReady?: () => void
  slug: string
  token: string
  pollsById: Record<string, PollSummary>
  tugsById: Record<string, TugSummary>
  boardsById: Record<string, ScoreboardSummary>
  giveawaysById: Record<string, GiveawaySummary>
  triviasById: Record<string, TriviaSummary>
}): JSX.Element | null => {
  const box = resolveElementBox(el)
  if (el.elementKind === 'CHAT') {
    const clipW = box.layoutWidth
    const clipH = el.h == null ? ELEMENT_BASE.CHAT.h : Math.max(1, Math.round(el.h / box.glyphScale))
    return (
      <div style={elementFrameStyle(el, box.glyphScale, glideMs)}>
        <div style={clipBoxStyle(clipW, clipH, glideMs)}>
          <ChatOverlayView slug={slug} chatId={el.elementId} token={token} fillHost />
        </div>
      </div>
    )
  }
  if (el.elementKind === 'EMOTEWALL') {
    const clipW = box.layoutWidth
    const clipH = el.h == null ? ELEMENT_BASE.EMOTEWALL.h : Math.max(1, Math.round(el.h / box.glyphScale))
    return (
      <div style={elementFrameStyle(el, box.glyphScale, glideMs)}>
        <div style={clipBoxStyle(clipW, clipH, glideMs)}>
          <EmoteWallView slug={slug} emoteWallId={el.elementId} token={token} fillHost />
        </div>
      </div>
    )
  }
  if (el.elementKind === 'MERCH') {
    return (
      <MerchElementFrame
        el={el}
        glyphScale={box.glyphScale}
        layoutWidth={box.layoutWidth}
        glideMs={glideMs}
        slug={slug}
        token={token}
        onContentReady={onContentReady}
      />
    )
  }
  if (el.elementKind === 'MERCH_TICKER') {
    return (
      <MerchElementFrame
        el={el}
        glyphScale={box.glyphScale}
        layoutWidth={box.layoutWidth}
        glideMs={glideMs}
        slug={slug}
        token={token}
        onContentReady={onContentReady}
      />
    )
  }
  const framed = (content: JSX.Element): JSX.Element => (
    <div style={elementFrameStyle(el, box.glyphScale, glideMs)}>{content}</div>
  )
  if (el.elementKind === 'POLL') {
    const data = pollsById[el.elementId]
    if (data === undefined) return null
    return framed(<PollElement data={data} layoutWidth={box.layoutWidth} />)
  }
  if (el.elementKind === 'TUG') {
    const data = tugsById[el.elementId]
    if (data === undefined) return null
    return framed(<TugElement data={data} layoutWidth={box.layoutWidth} />)
  }
  if (el.elementKind === 'GIVEAWAY') {
    const data = giveawaysById[el.elementId]
    if (data === undefined) return null
    return framed(<GiveawayElement data={data} layoutWidth={box.layoutWidth} />)
  }
  if (el.elementKind === 'TRIVIA') {
    const data = triviasById[el.elementId]
    if (data === undefined) return null
    return framed(<TriviaElement data={data} layoutWidth={box.layoutWidth} />)
  }
  const data = boardsById[el.elementId]
  if (data === undefined) return null
  return framed(<BoardElement data={data} layoutWidth={box.layoutWidth} />)
}

const NO_ELEMENTS: SceneElement[] = []

const SceneCanvas = ({
  overlay,
  scene,
  takeId,
  transition,
  slug,
  token,
  pollsById,
  tugsById,
  boardsById,
  giveawaysById,
  triviasById
}: {
  overlay: OverlayMeta
  scene: LiveSceneSnapshot | null
  takeId: string | null
  transition: SceneTransition
  slug: string
  token: string
  pollsById: Record<string, PollSummary>
  tugsById: Record<string, TugSummary>
  boardsById: Record<string, ScoreboardSummary>
  giveawaysById: Record<string, GiveawaySummary>
  triviasById: Record<string, TriviaSummary>
}): JSX.Element => {
  const { items, onContentReady } = useScenePresence({
    takeId,
    elements: scene != null ? scene.elements : NO_ELEMENTS,
    transition
  })
  return (
    <ScaledStage mode='overlay'>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: overlay.width,
          height: overlay.height,
          overflow: 'hidden'
        }}
      >
        {items.map((item) => (
          <PresenceLayer key={item.key} item={item}>
            <SceneElementFrame
              el={item.el}
              glideMs={item.glideMs}
              onContentReady={() => onContentReady(item.key)}
              slug={slug}
              token={token}
              pollsById={pollsById}
              tugsById={tugsById}
              boardsById={boardsById}
              giveawaysById={giveawaysById}
              triviasById={triviasById}
            />
          </PresenceLayer>
        ))}
      </div>
    </ScaledStage>
  )
}

export const CompositeOverlayPage = ({ kind = 'DESKTOP' }: { kind?: CompositeOverlayKind }): JSX.Element => {
  const { slug } = useParams<{ slug: string }>()
  const token = useOverlaySocketCredential()

  const [overlayState, setOverlayState] = useState<OverlayState | null>(null)
  const overlayMeta = overlayState != null ? overlayState.meta : null
  const [pollsById, setPollsById] = useState<Record<string, PollSummary>>({})
  const [tugsById, setTugsById] = useState<Record<string, TugSummary>>({})
  const [boardsById, setBoardsById] = useState<Record<string, ScoreboardSummary>>({})
  const [giveawaysById, setGiveawaysById] = useState<Record<string, GiveawaySummary>>({})
  const [triviasById, setTriviasById] = useState<Record<string, TriviaSummary>>({})
  const [scenesHaveTrivia, setScenesHaveTrivia] = useState(false)
  const hydrateMarkRef = useRef<{ takeId: string | null, version: number } | null>(null)

  const applyBootstrap = useCallback((data: OverlayRenderData): void => {
    if (data.overlay !== null) {
      const { scenes, ...meta } = data.overlay
      setOverlayState((p) => foldOverlayState(p, meta, 'CUT'))
      setScenesHaveTrivia(placesTrivia(scenes.map((s) => s.elements)))
      const mark = hydrateMarkRef.current
      if (mark == null || meta.version >= mark.version) {
        hydrateMarkRef.current = { takeId: takeIdOf(meta.liveScene), version: meta.version }
      }
    }
    const referencedElements = data.overlay !== null
      ? [
          ...data.overlay.scenes.flatMap((s) => s.elements),
          ...(data.overlay.liveScene !== null ? data.overlay.liveScene.elements : [])
        ]
      : []
    setPollsById((prev) => pruneDanglingRefs(
      foldElementList(prev, data.polls, (p) => p.pollId),
      referencedIds(referencedElements, 'POLL'),
      new Set(data.polls.map((p) => p.pollId))
    ))
    setTugsById((prev) => pruneDanglingRefs(
      foldElementList(prev, data.tugs, (t) => t.tugId),
      referencedIds(referencedElements, 'TUG'),
      new Set(data.tugs.map((t) => t.tugId))
    ))
    setBoardsById((prev) => pruneDanglingRefs(
      foldElementList(prev, data.scoreboards, (b) => b.boardId),
      referencedIds(referencedElements, 'BOARD'),
      new Set(data.scoreboards.map((b) => b.boardId))
    ))
    const giveaways = data.giveaways ?? []
    setGiveawaysById((prev) => pruneDanglingRefs(
      foldElementList(prev, giveaways, (g) => g.giveawayId),
      referencedIds(referencedElements, 'GIVEAWAY'),
      new Set(giveaways.map((g) => g.giveawayId))
    ))
    const trivias = data.trivias ?? []
    setTriviasById((prev) => pruneDanglingRefs(
      foldElementList(prev, trivias, (t) => t.triviaId),
      referencedIds(referencedElements, 'TRIVIA'),
      new Set(trivias.map((t) => t.triviaId))
    ))
  }, [])

  const refetchBootstrap = useCallback(async (): Promise<void> => {
    if (slug == null || slug === '' || token == null || token === '') return
    const res = await graphqlRequest<{ renderData: OverlayRenderData | null }>(
      getOverlayForRenderQuery, { slug, kind }, token
    )
    if (res.renderData === null) {
      log({ stage: 'overlay-null', detail: 'renderData null unknown slug or no overlay row for this event' })
      return
    }
    if (res.renderData.overlay !== null && res.renderData.overlay.overlayId !== kind) {
      log({
        stage: 'kind-mismatch',
        detail: 'error kind mismatch',
        expected: kind,
        got: res.renderData.overlay.overlayId
      })
      return
    }
    if (res.renderData.overlay === null) {
      log({ stage: 'overlay-null', detail: 'overlay row null in renderData' })
    } else if (res.renderData.overlay.liveScene === null) {
      log({
        stage: 'liveScene-null',
        detail: 'overlay exists but carries no live-scene snapshot, take a scene live'
      })
    }
    applyBootstrap(res.renderData)
  }, [slug, token, kind, applyBootstrap])

  useEffect(() => {
    if (slug == null || slug === '' || token == null || token === '') {
      log({
        stage: 'token-missing',
        detail: 'slug and/or ?token= URL param missing',
        hasSlug: slug != null && slug !== '',
        hasToken: token != null && token !== ''
      })
    }
  }, [slug, token])

  useEffect(() => {
    let cancelled = false
    let retryTimer: ReturnType<typeof globalThis.setTimeout> | undefined
    const load = (): void => {
      refetchBootstrap()
        .catch((err: unknown) => {
          log({ stage: 'bootstrap-error', err: err instanceof Error ? err.name : 'unknown', retryInMs: BOOTSTRAP_RETRY_MS })
          if (!cancelled) retryTimer = globalThis.setTimeout(load, BOOTSTRAP_RETRY_MS)
        })
    }
    load()
    return () => {
      cancelled = true
      globalThis.clearTimeout(retryTimer)
    }
  }, [refetchBootstrap])

  const handleConnected = useCallback((): void => {
    void refetchBootstrap()
      .catch(() => { })
  }, [refetchBootstrap])

  const structureChannel = slug != null && token != null && token !== '' ? `${slug}:overlay:${kind.toLowerCase()}:overlay` : null
  useOverlayStructureEvents(structureChannel, token, kind, {
    onOverlay: (env) => {
      setOverlayState((prev) => foldOverlayState(prev, env.overlay, env.transition ?? 'CUT'))
    },
    onConnected: handleConnected
  })

  const pollsChannel = slug != null && token != null && token !== '' ? `${slug}:polls:overlay` : null
  useChannelEvents<PollSummary>(pollsChannel, token, {
    onUpdate: (el) => {
      setPollsById((prev) => foldElement(prev, el.pollId, el))
    },
    onDelete: (id) => {
      setPollsById((prev) => removeElement(prev, id))
    },
    onConnected: handleConnected
  })

  const tugsChannel = slug != null && token != null && token !== '' ? `${slug}:tugs:overlay` : null
  useChannelEvents<TugSummary>(tugsChannel, token, {
    onUpdate: (el) => {
      setTugsById((prev) => foldElement(prev, el.tugId, el))
    },
    onDelete: (id) => {
      setTugsById((prev) => removeElement(prev, id))
    },
    onConnected: handleConnected
  })

  const boardsChannel = slug != null && token != null && token !== '' ? `${slug}:scoreboards:overlay` : null
  useChannelEvents<ScoreboardSummary>(boardsChannel, token, {
    onUpdate: (el) => {
      setBoardsById((prev) => foldElement(prev, el.boardId, el))
    },
    onDelete: (id) => {
      setBoardsById((prev) => removeElement(prev, id))
    },
    onConnected: handleConnected
  })

  const giveawaysChannel = slug != null && token != null && token !== '' ? `${slug}:giveaways:overlay` : null
  useChannelEvents<GiveawaySummary>(giveawaysChannel, token, {
    onUpdate: (el) => {
      setGiveawaysById((prev) => foldElement(prev, el.giveawayId, el))
    },
    onDelete: (id) => {
      setGiveawaysById((prev) => removeElement(prev, id))
    },
    onConnected: handleConnected
  })

  const liveElements = overlayMeta?.liveScene?.elements
  const liveHasTrivia = liveElements !== undefined && placesTrivia([liveElements])
  const triviaPlaced = liveHasTrivia || scenesHaveTrivia
  const triviaChannel = slug != null && token != null && token !== '' && triviaPlaced ? `${slug}:trivia:overlay` : null
  const triviaFeed = useChannelEvents<TriviaSummary>(triviaChannel, token, {
    onUpdate: (el) => {
      setTriviasById((prev) => foldElement(prev, el.triviaId, el))
    },
    onDelete: (id) => {
      setTriviasById((prev) => removeElement(prev, id))
    },
    onConnected: handleConnected
  })
  const triviaFeedError = triviaFeed.error
  useEffect(() => {
    if (triviaFeedError === null) return
    log({ stage: 'trivia-feed-refused', detail: TRIVIA_FEED_REFUSED_DETAIL, err: triviaFeedError.name })
  }, [triviaFeedError])

  useEffect(() => {
    if (overlayState == null || overlayState.takeId == null) return
    const mark = hydrateMarkRef.current
    if (mark != null && mark.takeId === overlayState.takeId) return
    const scene = overlayState.meta.liveScene
    const unknown = scene?.elements.some((el) =>
      (el.elementKind === 'POLL' && pollsById[el.elementId] === undefined) ||
      (el.elementKind === 'TUG' && tugsById[el.elementId] === undefined) ||
      (el.elementKind === 'BOARD' && boardsById[el.elementId] === undefined) ||
      (el.elementKind === 'GIVEAWAY' && giveawaysById[el.elementId] === undefined) ||
      (el.elementKind === 'TRIVIA' && triviasById[el.elementId] === undefined)
    ) ?? false
    if (!unknown) return
    hydrateMarkRef.current = { takeId: overlayState.takeId, version: overlayState.meta.version }
    void refetchBootstrap()
      .catch((err: unknown) => {
        log({ stage: 'hydrate-refetch-failed', err: err instanceof Error ? err.name : 'unknown' })
      })
  }, [overlayState, pollsById, tugsById, boardsById, giveawaysById, triviasById, refetchBootstrap])

  const liveScene = overlayMeta !== null ? overlayMeta.liveScene : null

  if (slug == null || slug === '' || token == null || token === '') {
    return <OverlayShell>{null}</OverlayShell>
  }

  return (
    <OverlayShell>
      <OverlayAutoFit
        mode={overlayMeta !== null
          ? { kind: 'design', width: overlayMeta.width, height: overlayMeta.height }
          : { kind: 'off' }}
      >
        {overlayState != null
          ? (
            <SceneCanvas
              overlay={overlayState.meta}
              scene={liveScene}
              takeId={overlayState.takeId}
              transition={overlayState.transition}
              slug={slug}
              token={token}
              pollsById={pollsById}
              tugsById={tugsById}
              boardsById={boardsById}
              giveawaysById={giveawaysById}
              triviasById={triviasById}
            />
            )
          : null}
      </OverlayAutoFit>
    </OverlayShell>
  )
}
